/**
 * LegalAcceptanceGate Component
 *
 * Wraps children and blocks rendering until the authenticated user has accepted
 * all required legal documents (Terms of Service, Privacy Policy, etc.).
 *
 * Flow:
 *  1. On mount, calls GET /api/legal/status.
 *  2. If `allAccepted` is true, children render immediately.
 *  3. Otherwise, a full-screen overlay lists pending required documents.
 *  4. Clicking "View & Accept" opens a Dialog with the full document content.
 *  5. After scrolling to the bottom, the user clicks "I Accept".
 *  6. POST /api/legal/accept records each acceptance individually.
 *  7. Once all required documents are accepted, the gate clears and children render.
 *
 * Usage:
 *   <LegalAcceptanceGate>
 *     <App />
 *   </LegalAcceptanceGate>
 *
 *   // Skip the gate on admin/public pages:
 *   <LegalAcceptanceGate enabled={false}>
 *     <AdminPage />
 *   </LegalAcceptanceGate>
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import DOMPurify from 'dompurify';
import { FileText, CheckCircle, AlertTriangle, Shield } from 'lucide-react';
import { AxiosError } from 'axios';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import api from '../api/axiosConfig';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A single document's acceptance status as returned by GET /api/legal/status */
interface DocumentStatus {
  id: string;
  slug: string;
  type: string;
  title: string;
  version: string;
  isRequired: boolean;
  accepted: boolean;
  acceptedAt: string | null;
}

/** Full response shape from GET /api/legal/status */
interface LegalStatusResponse {
  allAccepted: boolean;
  documents: DocumentStatus[];
}

/** Full document data returned by GET /api/legal/documents/:slug */
interface LegalDocumentContent {
  id: string;
  slug: string;
  type: string;
  title: string;
  content: string;
  version: string;
  isRequired: boolean;
  effectiveDate: string;
}

export interface LegalAcceptanceGateProps {
  children: React.ReactNode;
  /** Default true. Set false to bypass the gate (e.g., for admin/public pages). */
  enabled?: boolean;
  /** Current user ID — used to scope dismiss-tracking per user in localStorage. */
  userId?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function extractErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof AxiosError) {
    return (
      (err.response?.data as { message?: string })?.message ||
      err.message ||
      fallback
    );
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

function formatDocType(type: string): string {
  switch (type) {
    case 'terms':  return 'Terms of Service';
    case 'privacy': return 'Privacy Policy';
    case 'aup':    return 'Acceptable Use Policy';
    case 'dpa':    return 'Data Processing Agreement';
    case 'sla':    return 'Service Level Agreement';
    default:       return type.replace(/_/g, ' ');
  }
}

// ---------------------------------------------------------------------------
// Dismiss-tracking helpers — "Remind me later" feature
// ---------------------------------------------------------------------------

const MAX_DISMISSALS = 3;
const DISMISS_STORAGE_PREFIX = 'legal_gate_dismissals_';

interface DismissState {
  count: number;
  lastDismissed: string;
}

function getDismissKey(userId?: string): string {
  return userId ? `${DISMISS_STORAGE_PREFIX}${userId}` : `${DISMISS_STORAGE_PREFIX}anonymous`;
}

function getDismissState(userId?: string): DismissState {
  try {
    const raw = localStorage.getItem(getDismissKey(userId));
    if (raw) return JSON.parse(raw) as DismissState;
  } catch { /* ignore */ }
  return { count: 0, lastDismissed: '' };
}

function saveDismissState(state: DismissState, userId?: string): void {
  try {
    localStorage.setItem(getDismissKey(userId), JSON.stringify(state));
  } catch { /* quota exceeded */ }
}

function clearDismissState(userId?: string): void {
  try {
    localStorage.removeItem(getDismissKey(userId));
  } catch { /* ignore */ }
}

// ---------------------------------------------------------------------------
// Sub-component: Full-screen loading overlay
// ---------------------------------------------------------------------------

function LoadingOverlay() {
  return (
    <div
      role="status"
      aria-label="Checking legal document acceptance"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background"
    >
      <div className="flex flex-col items-center gap-4">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-muted border-t-primary" />
        <p className="text-sm text-muted-foreground">Checking agreements…</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: Full-screen error screen
// ---------------------------------------------------------------------------

interface ErrorScreenProps {
  message: string;
  onRetry: () => void;
  onSkip: () => void;
}

function ErrorScreen({ message, onRetry, onSkip }: ErrorScreenProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background px-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-3">
            <AlertTriangle className="size-6 shrink-0 text-destructive" />
            <CardTitle>Unable to load agreements</CardTitle>
          </div>
          <CardDescription>{message}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          <Button onClick={onRetry} variant="outline" className="w-full">
            Retry
          </Button>
          <Button onClick={onSkip} variant="ghost" className="w-full text-muted-foreground">
            Continue to portal
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: Document viewer dialog with scroll-to-bottom gate
// ---------------------------------------------------------------------------

interface DocumentDialogProps {
  /** The pending document entry (id, slug, title, version) */
  doc: DocumentStatus;
  /** Whether this dialog is open */
  open: boolean;
  /** Called when the dialog should close without accepting */
  onClose: () => void;
  /** Called when the user clicks "I Accept" — must throw on failure */
  onAccept: (doc: DocumentStatus) => Promise<void>;
}

function DocumentDialog({ doc, open, onClose, onAccept }: DocumentDialogProps) {
  const [content, setContent] = useState<LegalDocumentContent | null>(null);
  const [loadingContent, setLoadingContent] = useState(false);
  const [contentError, setContentError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  // Whether the user has scrolled close enough to the bottom to enable acceptance
  const [hasScrolled, setHasScrolled] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Fetch the full document content each time the dialog opens
  useEffect(() => {
    if (!open) {
      // Reset all state when closed so the next open starts fresh
      setContent(null);
      setContentError(null);
      setAcceptError(null);
      setHasScrolled(false);
      return;
    }

    let cancelled = false;

    async function fetchContent() {
      setLoadingContent(true);
      setContentError(null);
      try {
        const res = await api.get<{ success: boolean; data: LegalDocumentContent }>(
          `/legal/documents/${encodeURIComponent(doc.slug)}`
        );
        if (res.data?.success === false) {
          if (!cancelled) setContentError('Failed to load document content.');
          return;
        }
        if (!cancelled) setContent(res.data.data);
      } catch (err) {
        if (!cancelled) {
          setContentError(extractErrorMessage(err, 'Failed to load document content.'));
        }
      } finally {
        if (!cancelled) setLoadingContent(false);
      }
    }

    void fetchContent();
    return () => { cancelled = true; };
  }, [open, doc.slug]);

  // Allow acceptance if the document fits on-screen without scrolling
  useEffect(() => {
    if (!content) return;
    const el = scrollRef.current;
    if (!el) return;
    const timer = setTimeout(() => {
      if (el.scrollHeight <= el.clientHeight + 60) {
        setHasScrolled(true);
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [content]);

  // Track scroll position to detect reaching the bottom
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 60;
    if (nearBottom) setHasScrolled(true);
  }, []);

  const handleAccept = async () => {
    setAccepting(true);
    setAcceptError(null);
    try {
      await onAccept(doc);
      // Parent updates state and closes dialog on success
    } catch (err) {
      setAcceptError(extractErrorMessage(err, 'Failed to record acceptance. Please try again.'));
    } finally {
      setAccepting(false);
    }
  };

  // Sanitize HTML content from the API using DOMPurify before rendering.
  // Content originates from admin-seeded D1 database only (not user input),
  // but we sanitize defensively against any future content changes.
  const sanitizedContent = content
    ? DOMPurify.sanitize(content.content, { USE_PROFILES: { html: true } })
    : '';

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent
        className={cn(
          'flex max-h-[90vh] w-full max-w-3xl flex-col gap-0 p-0',
          'sm:max-w-3xl'
        )}
        showCloseButton={false}
      >
        {/* Header */}
        <DialogHeader className="shrink-0 border-b px-6 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex min-w-0 items-center gap-3">
              <FileText className="size-5 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <DialogTitle className="truncate">{doc.title}</DialogTitle>
                <DialogDescription className="mt-0.5">
                  Version {doc.version}&nbsp;&mdash;&nbsp;{formatDocType(doc.type)}
                </DialogDescription>
              </div>
            </div>
            <Badge
              variant={doc.isRequired ? 'default' : 'secondary'}
              className="shrink-0"
            >
              {doc.isRequired ? 'Required' : 'Optional'}
            </Badge>
          </div>
        </DialogHeader>

        {/* Scrollable content */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="min-h-0 flex-1 overflow-y-auto px-6 py-4"
        >
          {loadingContent && (
            <div className="flex items-center justify-center py-16">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-primary" />
            </div>
          )}

          {!loadingContent && contentError && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
              {contentError}
            </div>
          )}

          {!loadingContent && content && (
            <div
              className={cn(
                'prose prose-sm dark:prose-invert max-w-none',
                'prose-headings:font-semibold prose-headings:text-foreground',
                'prose-h1:text-xl prose-h2:text-lg prose-h2:mt-6',
                'prose-p:text-muted-foreground prose-p:leading-relaxed',
                'prose-li:text-muted-foreground',
                'prose-strong:text-foreground'
              )}
              // Content is sanitized by DOMPurify above before rendering
              dangerouslySetInnerHTML={{ __html: sanitizedContent }}
            />
          )}
        </div>

        {/* Footer */}
        <DialogFooter className="shrink-0 border-t px-6 py-4">
          <div className="flex w-full flex-col gap-3">
            {!hasScrolled && content && (
              <p className="text-center text-xs text-muted-foreground">
                Please scroll to the bottom of the document before accepting.
              </p>
            )}

            {acceptError && (
              <p className="text-center text-xs text-destructive">{acceptError}</p>
            )}

            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button variant="outline" onClick={onClose} disabled={accepting}>
                Cancel
              </Button>
              <Button
                onClick={handleAccept}
                disabled={accepting || loadingContent || !content || !hasScrolled}
              >
                {accepting ? (
                  <>
                    <span className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
                    Accepting…
                  </>
                ) : (
                  <>
                    <CheckCircle className="mr-1 size-4" />
                    I Accept
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function LegalAcceptanceGate({
  children,
  enabled = true,
  userId,
}: LegalAcceptanceGateProps) {
  const [status, setStatus] = useState<LegalStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // The document currently open in the viewer Dialog (null = none open)
  const [activeDoc, setActiveDoc] = useState<DocumentStatus | null>(null);
  // Dismiss tracking — user can postpone acceptance up to MAX_DISMISSALS times
  const [dismissed, setDismissed] = useState(false);
  const [dismissCount, setDismissCount] = useState(() => getDismissState(userId).count);

  // ---------------------------------------------------------------------------
  // Fetch acceptance status
  // ---------------------------------------------------------------------------
  const fetchStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ success: boolean; data: LegalStatusResponse }>(
        '/legal/status'
      );
      if (res.data?.success === false) {
        setError('Failed to load agreement status. Please try again.');
        return;
      }
      setStatus(res.data.data);
    } catch (err) {
      // On 401/403, fail open — let the user through. Auth middleware elsewhere
      // will handle the redirect to login if the session is truly expired.
      if (err instanceof AxiosError && (err.response?.status === 401 || err.response?.status === 403)) {
        setStatus({ allAccepted: true, documents: [] });
        return;
      }
      setError(extractErrorMessage(err, 'Failed to load agreement status. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, []);

  // Re-read dismiss count when userId changes (e.g., different user logs in)
  useEffect(() => {
    setDismissCount(getDismissState(userId).count);
    setDismissed(false);
  }, [userId]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      setStatus(null);     // Clear stale status on logout
      setDismissed(false); // Reset dismiss state on logout
      return;
    }
    void fetchStatus();
  }, [enabled, fetchStatus]);

  // ---------------------------------------------------------------------------
  // Record acceptance for a single document
  // ---------------------------------------------------------------------------
  const handleAccept = useCallback(async (doc: DocumentStatus) => {
    const res = await api.post<{
      success: boolean;
      data: { accepted: boolean; documentId: string; documentVersion: string };
    }>('/legal/accept', {
      documentId: doc.id,
      documentVersion: doc.version,
    });

    if (res.data?.success === false) {
      throw new Error('Server did not confirm acceptance. Please try again.');
    }

    // Optimistically update local status
    setStatus((prev) => {
      if (!prev) return prev;
      const updated = prev.documents.map((d) =>
        d.id === doc.id
          ? { ...d, accepted: true, acceptedAt: new Date().toISOString() }
          : d
      );
      const allAccepted = updated
        .filter((d) => d.isRequired)
        .every((d) => d.accepted);
      // Reset dismissal count when all required docs are accepted
      if (allAccepted) clearDismissState(userId);
      return { allAccepted, documents: updated };
    });

    // Close the dialog
    setActiveDoc(null);
  }, []);

  // Skip the gate entirely — used when the API is unreachable or returns errors
  const handleSkip = useCallback(() => {
    setStatus({ allAccepted: true, documents: [] });
    setError(null);
  }, []);

  // "Remind me later" — dismiss the gate for this session (up to MAX_DISMISSALS times)
  const handleDismiss = useCallback(() => {
    const newCount = dismissCount + 1;
    saveDismissState({ count: newCount, lastDismissed: new Date().toISOString() }, userId);
    setDismissCount(newCount);
    setDismissed(true);
  }, [dismissCount, userId]);

  // ---------------------------------------------------------------------------
  // Bypass: gate disabled
  // ---------------------------------------------------------------------------
  if (!enabled) {
    return <>{children}</>;
  }

  // ---------------------------------------------------------------------------
  // Loading
  // ---------------------------------------------------------------------------
  if (loading) {
    return <LoadingOverlay />;
  }

  // ---------------------------------------------------------------------------
  // Error — show retry + skip option (never block the user completely)
  // ---------------------------------------------------------------------------
  if (error) {
    return <ErrorScreen message={error} onRetry={fetchStatus} onSkip={handleSkip} />;
  }

  // ---------------------------------------------------------------------------
  // Status not yet fetched — treat as loading (fixes race condition when
  // enabled transitions from false→true: loading=false before fetchStatus runs)
  // ---------------------------------------------------------------------------
  if (!status) {
    return <LoadingOverlay />;
  }

  // ---------------------------------------------------------------------------
  // All accepted — render children
  // ---------------------------------------------------------------------------
  if (status.allAccepted) {
    return <>{children}</>;
  }

  // ---------------------------------------------------------------------------
  // Dismissed — user chose "Remind me later" this session
  // ---------------------------------------------------------------------------
  if (dismissed) {
    return <>{children}</>;
  }

  // ---------------------------------------------------------------------------
  // Gate UI — pending documents exist
  // ---------------------------------------------------------------------------
  const pendingRequired = status.documents.filter((d) => d.isRequired && !d.accepted);
  const pendingOptional = status.documents.filter((d) => !d.isRequired && !d.accepted);
  const acceptedDocs    = status.documents.filter((d) => d.accepted);

  return (
    <>
      {/* Full-screen overlay — blocks access until all required docs are accepted */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Legal document acceptance required"
        className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-background/95 px-4 py-8 backdrop-blur-sm"
      >
        <div className="mx-auto w-full max-w-lg">

          {/* Header */}
          <div className="mb-6 flex flex-col items-center gap-3 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
              <Shield className="size-7 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-foreground">
                Review &amp; Accept Agreements
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Please review and accept the following documents to continue using the portal.
              </p>
            </div>
          </div>

          {/* Required documents list */}
          {pendingRequired.length > 0 && (
            <Card className="mb-4">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <FileText className="size-4 text-muted-foreground" />
                  <CardTitle className="text-base">Required Documents</CardTitle>
                </div>
                <CardDescription>
                  You must accept all required documents before continuing.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-3">
                {pendingRequired.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between gap-3 rounded-lg border p-3"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-foreground">
                        {doc.title}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Version {doc.version}&nbsp;&middot;&nbsp;{formatDocType(doc.type)}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <Badge variant="default" className="text-xs">
                        Required
                      </Badge>
                      <Button size="sm" onClick={() => setActiveDoc(doc)}>
                        View &amp; Accept
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Optional documents list */}
          {pendingOptional.length > 0 && (
            <Card className="mb-4">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Optional Documents</CardTitle>
                <CardDescription>
                  These are optional but recommended reading.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-3">
                {pendingOptional.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between gap-3 rounded-lg border p-3"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-foreground">
                        {doc.title}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Version {doc.version}&nbsp;&middot;&nbsp;{formatDocType(doc.type)}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <Badge variant="secondary" className="text-xs">
                        Optional
                      </Badge>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setActiveDoc(doc)}
                      >
                        View &amp; Accept
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Already-accepted documents (positive feedback) */}
          {acceptedDocs.length > 0 && (
            <div className="mt-2 flex flex-col gap-1.5">
              {acceptedDocs.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center gap-2 text-xs text-muted-foreground"
                >
                  <CheckCircle className="size-3.5 shrink-0 text-green-600 dark:text-green-400" />
                  <span>{doc.title} — accepted</span>
                </div>
              ))}
            </div>
          )}

          {/* Remind me later / forced acceptance message */}
          {dismissCount < MAX_DISMISSALS ? (
            <div className="mt-4 text-center">
              <Button
                variant="ghost"
                onClick={handleDismiss}
                className="text-muted-foreground"
              >
                Remind me later
              </Button>
              <p className="mt-1 text-xs text-muted-foreground">
                You can skip this {MAX_DISMISSALS - dismissCount} more {MAX_DISMISSALS - dismissCount === 1 ? 'time' : 'times'}
              </p>
            </div>
          ) : (
            <div className="mt-4 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-center">
              <p className="text-sm font-medium text-destructive">
                You must accept these agreements to continue using the portal.
              </p>
            </div>
          )}

        </div>
      </div>

      {/* Document viewer dialog */}
      {activeDoc && (
        <DocumentDialog
          doc={activeDoc}
          open={activeDoc !== null}
          onClose={() => setActiveDoc(null)}
          onAccept={handleAccept}
        />
      )}
    </>
  );
}
